import React, { useCallback, useMemo, MouseEventHandler } from 'react'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import Group from 'components/utils/Group'
import useBusinessTravellerEmployeeQuery from 'businessTraveller/hooks/useEmployeeQuery'
import { sortBy } from 'lib/array/arrayUtils'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItem from 'LuxKit/List/ListItem'
import ListSubheader from 'LuxKit/List/ListSubheader'
import Divider from 'LuxKit/Divider'
import { useAppSelector } from 'hooks/reduxHooks'

interface Props {
  searchTerm?: string
  onAddEmployee: (employeeId: string) => void
  onRemoveEmployee: (employeeId: string) => void
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
}

function matchesSearch(employee: App.BusinessTraveller.EmployeeFromMeEndpoint, searchTerm: string) {
  const term = searchTerm.toLocaleLowerCase()
  return employee.firstName.toLocaleLowerCase().startsWith(term) ||
    employee.lastName.toLocaleLowerCase().startsWith(term)
}

function TravellersContent(props: Props) {
  const { searchTerm = '', travellerEmployees, onAddEmployee, onRemoveEmployee } = props
  const { employees } = useBusinessTravellerEmployeeQuery()
  const currentEmployeeId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.employee?.id)
  const businessName = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.name)

  const searchActive = !!searchTerm

  const { currentUserOption, otherEmployeeOptions, totalEmployeeCount } = useMemo(() => {
    const allEmployees = Object.values(employees ?? {})
    const currentUser = currentEmployeeId ? allEmployees.find((employee) => employee.id === currentEmployeeId) : undefined
    const others = sortBy(
      allEmployees.filter((employee) => employee.id !== currentEmployeeId),
      (employee) => employee.firstName + employee.lastName,
      'asc',
    )

    if (searchTerm) {
      return {
        currentUserOption: currentUser && matchesSearch(currentUser, searchTerm) ? currentUser : undefined,
        otherEmployeeOptions: others.filter((employee) => matchesSearch(employee, searchTerm)),
        totalEmployeeCount: allEmployees.length,
      }
    }

    return { currentUserOption: currentUser, otherEmployeeOptions: others, totalEmployeeCount: allEmployees.length }
  }, [employees, currentEmployeeId, searchTerm])

  const handleEmployeeClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const employeeId = event.currentTarget.dataset.employeeid
    if (employeeId) {
      if (travellerEmployees.some(employee => employee.id === employeeId)) {
        onRemoveEmployee(employeeId)
      } else {
        onAddEmployee(employeeId)
      }
    }
  }, [travellerEmployees, onAddEmployee, onRemoveEmployee])

  const renderEmployeeItem = (employeeOption: App.BusinessTraveller.EmployeeFromMeEndpoint) => (
    <ClickableListItem
      key={employeeOption.id}
      size="large"
      startIcon={<AvatarCircle
        firstName={employeeOption.firstName}
        lastName={employeeOption.lastName}
      />}
      title={`${employeeOption.firstName} ${employeeOption.lastName}`}
      subtitle={employeeOption.email}
      data-employeeid={employeeOption.id}
      onClick={handleEmployeeClick}
      endIcon={<CheckboxButton checked={travellerEmployees.some(employee => employee.id === employeeOption.id)} />}
    />
  )

  const showNoResults = searchActive && !currentUserOption && otherEmployeeOptions.length === 0

  return (
    <Group direction="vertical" gap={12}>
      {currentUserOption && renderEmployeeItem(currentUserOption)}
      {otherEmployeeOptions.length > 0 && <>
        {currentUserOption && <Divider kind="primary" />}
        <ListSubheader>
          {businessName ? `${businessName}'s team members (${totalEmployeeCount})` : `Team members (${totalEmployeeCount})`}
        </ListSubheader>
        {otherEmployeeOptions.map(renderEmployeeItem)}
      </>}
      {showNoResults && <ListItem titleColour="neutral-two" title={`No results found for "${searchTerm}"`} />}
    </Group>
  )
}

export default TravellersContent
